<template>
  <v-container class="pluviometric-chart-container">
    <div
      v-if="pdfUrl"
      ref="pdfWrapper"
      class="pdf-wrapper"
      @mouseenter="showControls"
      @mouseleave="startHideTimer"
      @mousemove="showControls"
      @scroll="handleScroll"
    >
      <div class="pdf-container">
        <vue-pdf-embed
          ref="pdfEmbed"
          :source="pdfUrl"
          :width="calculatedWidth"
          :text-layer="true"
          :annotation-layer="true"
          @loaded="handleDocumentLoad"
        />
      </div>
    </div>

    <div v-else class="no-pdf-message">
      <p>No hay PDF disponible</p>
    </div>

    <div
      v-if="pdfUrl && showToolbar"
      class="pdf-toolbar"
      :class="{ 'fade-in': showToolbar, 'fade-out': !showToolbar }"
      @mouseenter="showControls"
      @mouseleave="startHideTimer"
    >
      <div class="toolbar-section">
        <v-btn icon small :disabled="page <= 1" @click="scrollToPage(page - 1)">
          <v-icon color="white" small>mdi-chevron-left</v-icon>
        </v-btn>
        
        <span class="page-info">{{ page }} / {{ pageCount }}</span>
        
        <v-btn icon small :disabled="page >= pageCount" @click="scrollToPage(page + 1)">
          <v-icon color="white" small>mdi-chevron-right</v-icon>
        </v-btn>
      </div>

      <div class="toolbar-divider"></div>

      <div class="toolbar-section">
        <v-btn
          icon
          small
          :disabled="scale <= 0.5"
          @click="zoomOut"
          class="toolbar-btn"
        >
          <v-icon small>mdi-minus</v-icon>
        </v-btn>
        
        <span class="zoom-info">{{ Math.round(scale * 100) }}%</span>
        
        <v-btn
          icon
          small
          :disabled="scale >= 3"
          @click="zoomIn"
          class="toolbar-btn"
        >
          <v-icon small>mdi-plus</v-icon>
        </v-btn>
      </div>
    </div>
  </v-container>
</template>

<script>
import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed'
export default {
  name: 'PdfViewer',
  props: {
    pdfUrl: {
      type: String,
      required: true
    }
  },
  components: {
    VuePdfEmbed
  },
  data () {
    return {
      page: 1,
      pageCount: 0,
      scale: 1,
      baseWidth: 800, // Default width before calculation
      showToolbar: false,
      hideTimer: null,
      disableZoom: false
    }
  },
  computed: {
    calculatedWidth() {
      return Math.floor(this.baseWidth * this.scale)
    }
  },
  watch: {
    // Reset page when PDF changes
    pdfUrl(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.page = 1
        this.scale = 1
        this.$nextTick(() => {
          this.updateBaseWidth(!oldVal && newVal ? 24 : undefined)
        })
      }
    }
  },
  mounted() {
    if (this.$refs.pdfWrapper && this.$refs.pdfWrapper.clientWidth) {
      this.updateBaseWidth()
    } else {
      // Wait for wrapper rendering (Expansion panel delays it)
      setTimeout(() => {
        this.updateBaseWidth()
      }, 100)
    }
    window.addEventListener('resize', this.updateBaseWidth)
  },
  beforeDestroy() {
    this.clearHideTimer()
    window.removeEventListener('resize', this.updateBaseWidth)
  },
  methods: {
    // Determine the width of the container so zoom starts at "100%" relative to container
    updateBaseWidth(offset) {
      if (this.$refs.pdfWrapper) {
        // Subtract padding (approx 28px) from the wrapper width
        offset = typeof offset === 'number' ? offset : 28
        this.baseWidth = this.$refs.pdfWrapper.clientWidth - offset
      }
    },
    handleDocumentLoad(pdfDocument) {
      // vue-pdf-embed returns the PDF document object on load
      this.pageCount = pdfDocument.numPages
    },
    
    showControls() {
      this.clearHideTimer()
      this.showToolbar = true
    },
    startHideTimer() {
      this.clearHideTimer()
      this.hideTimer = setTimeout(() => {
        this.showToolbar = false
      }, 2000)
    },
    clearHideTimer() {
      if (this.hideTimer) {
        clearTimeout(this.hideTimer)
        this.hideTimer = null
      }
    },
    zoomIn() {
      if (this.disableZoom) return
      if (this.scale < 3) {
        this.scale += 0.25
        this.disableZoom = true
        setTimeout(() => {
          this.disableZoom = false
        }, 500)
      }
    },
    zoomOut() {
      if (this.disableZoom) return
      if (this.scale > 0.5) {
        this.scale -= 0.25
        this.disableZoom = true
        setTimeout(() => {
          this.disableZoom = false
        }, 500)
      }
    },

    scrollToPage(pageNum) {
      if (pageNum < 1 || pageNum > this.pageCount) return

      // Access the internal DOM of the component
      const container = this.$refs.pdfEmbed.$el
      
      // The library renders each page as a direct child <div> or <canvas>
      // Arrays are 0-indexed, so use index pageNum - 1
      const pageElement = container.children[pageNum - 1]

      if (pageElement) {
        pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
        this.page = pageNum
      }
    },

    handleScroll(e) {
      const wrapper = e.target
      const container = this.$refs.pdfEmbed && this.$refs.pdfEmbed.$el
      
      if (!wrapper || !container) return

      // Loop through the children of the single component instance
      const pages = container.children
      const wrapperRect = wrapper.getBoundingClientRect()

      for (let i = 0; i < pages.length; i++) {
        const page = pages[i]
        const rect = page.getBoundingClientRect()

        // Check if this page is in the middle of the view
        if (rect.top >= wrapperRect.top - 100 && rect.top < wrapperRect.bottom) {
          this.page = i + 1 // Index + 1 = Page Number
          break
        }
      }
    }
  }
}
</script>

<style scoped>
  .pluviometric-chart-container {
    position: relative;
    min-height: 500px;
  }
  
  .pdf-wrapper {
    position: relative;
    height: 500px;
    background-color: white; /* Standard PDF reader background color */
    border-radius: 4px;
    overflow: auto;
    /* Smooth scrolling */
    scroll-behavior: smooth;
  }
  
  .pdf-container {
    display: flex;
    justify-content: center;
    padding: 5px;
    /* Ensures content can expand beyond 100% when zoomed */
    width: fit-content; 
    min-width: 100%;
  }
  
  /* Styles for the component canvas internal */
  .vue-pdf-embed > div {
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
    margin-bottom: 20px;
    user-select: text;
    -webkit-user-select: text;
    -moz-user-select: text;
    -ms-user-select: text;
  }
  
  /* Allow text selection in PDF text layer */
  .vue-pdf-embed .textLayer {
    user-select: text !important;
    -webkit-user-select: text !important;
    -moz-user-select: text !important;
    -ms-user-select: text !important;
  }
  
  .vue-pdf-embed .textLayer span {
    user-select: text !important;
    -webkit-user-select: text !important;
    -moz-user-select: text !important;
    -ms-user-select: text !important;
    cursor: text;
  }
  
  /* --- Toolbar Styles --- */
  .pdf-toolbar {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.8);
    border-radius: 24px;
    padding: 2px 16px;
    display: flex;
    align-items: center;
    gap: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 10;
    border: 1px solid rgba(255, 255, 255, 0.2);
    width: fit-content;
  }
  
  .toolbar-section {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .toolbar-divider {
    width: 1px;
    height: 24px;
    background-color: rgba(255, 255, 255, 0.3);
  }
  
  .toolbar-btn {
    color: white !important;
    min-width: 32px !important;
    width: 32px !important;
    height: 32px !important;
  }
  
  .toolbar-btn:disabled {
    opacity: 0.4;
  }
  
  .page-info,
  .zoom-info {
    color: white;
    font-size: 14px;
    font-weight: 500;
    min-width: 60px;
    text-align: center;
    user-select: none;
  }
  
  .fade-in { animation: fadeIn 0.3s ease-in-out; }
  .fade-out { animation: fadeOut 0.3s ease-in-out; }
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateX(-50%) translateY(10px); }
    to { opacity: 1; transform: translateX(-50%) translateY(0); }
  }
  @keyframes fadeOut {
    from { opacity: 1; transform: translateX(-50%) translateY(0); }
    to { opacity: 0; transform: translateX(-50%) translateY(10px); }
  }
  
  .no-pdf-message {
    text-align: center;
    padding: 48px;
    color: #757575;
  }
</style>


// WEBPACK FOOTER //
// PdfViewer.vue?3dba17be